import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useSiteContent } from '@/context/SiteContentContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/pms/compat';
import hydraLogo from '@/assets/hydra-logo.png';
import ciWebGroupLogo from '@/assets/ci-web-group-logo-horizontal-white-rgb.svg';

/**
 * Custom Design mode footer — a simple, flat footer that's visually
 * distinct from the Hydra CMS-themed footers. Grid layout with
 * basic company info, links, and the standard branding bar.
 */
export const CustomFooter: React.FC = () => {
  const ctx = useSiteContent();
  const currentYear = new Date().getFullYear();

  const { data: bizSettings } = useQuery({
    queryKey: ['business-settings-header'],
    queryFn: async () => {
      const { data } = await supabase
        .from('business_settings')
        .select('company_name, phone, email, address_street, address_city, address_state, address_zip')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const companyName = bizSettings?.company_name || ctx.template.brand?.name || 'Company';
  const phone = bizSettings?.phone || ctx.template.contact?.phone || '';
  const email = bizSettings?.email || ctx.template.contact?.email || '';
  const address = bizSettings?.address_street || ctx.template.contact?.address || '';
  const city = bizSettings?.address_city || ctx.template.contact?.city || '';
  const state = bizSettings?.address_state || ctx.template.contact?.state || '';
  const zip = bizSettings?.address_zip || ctx.template.contact?.zip || '';

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">{companyName}</h3>
            <p className="text-sm text-gray-400 mb-4">
              {ctx.footerTagline || ctx.template.brand?.tagline || ''}
            </p>
            <div className="space-y-2 text-sm">
              {phone && (
                <a href={`tel:${phone.replace(/\D/g, '')}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Phone className="h-4 w-4" /> {phone}
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  <Mail className="h-4 w-4" /> {email}
                </a>
              )}
              {address && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{address}<br />{city}, {state} {zip}</span>
                </div>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              {[
                ['Services', '/services'],
                ['About', '/about'],
                ['Service Areas', '/service-areas'],
                ['Contact', '/contact'],
                ['Resources', '/resources'],
              ].map(([label, href]) => (
                <li key={href}>
                  <Link to={href} className="text-gray-400 hover:text-white transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link></li>
              <li><Link to="/sitemap" className="text-gray-400 hover:text-white transition-colors">Sitemap</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-10 pt-6 border-t border-gray-800 text-center text-sm text-gray-500">
          <p>&copy; {currentYear} {companyName}. All rights reserved.</p>
        </div>
      </div>

      {/* Branding Bar */}
      <div className="bg-black border-t border-white/10">
        <div className="container mx-auto px-4 py-5">
          <div className="flex items-center justify-between gap-4">
            <a
              href="https://getstarted.ciwebgroup.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative px-4 py-2 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 text-white text-xs sm:text-sm font-bold rounded-lg overflow-hidden transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.5)] hover:scale-105 whitespace-nowrap flex-shrink-0"
            >
              <span className="relative z-10 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="hidden sm:inline">Want a website like this?</span>
                <span className="sm:hidden">Get a Website</span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </a>
            <div className="flex items-center gap-3 sm:gap-4 ml-auto">
              <div className="flex items-center gap-2">
                <span className="text-white/50 text-xs whitespace-nowrap hidden md:inline">Powered by</span>
                <a href="https://ciwebgroup.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                  <img src={ciWebGroupLogo} alt="AI Website and AI Marketing Agency | Hydra Operating System" className="h-4 sm:h-5 w-auto" />
                </a>
              </div>
              <div className="w-px h-6 sm:h-8 bg-white/20" />
              <img src={hydraLogo} alt="Hydra OS" className="h-8 sm:h-10 w-auto flex-shrink-0" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
